import React, { useCallback } from "react";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { PaymentMethod } from "../../../../common/enums";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { useSetBankCountry } from "../../hooks/use-set-bank-country";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import { AccountDetails } from "./AccountDetails";
import { BankDetails } from "./BankDetails";
import { EftPaymentPrefInputSchema, type EftPaymentPrefInputType } from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const EftPaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Back",
}) => {
  const { setPaymentPrefDetail, workflowMode, selectedBankCountry } =
    useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  useReportError(error ? formatError(error) : null);

  /**
   * Resolves the bank country (Canada for EFT) and hydrates the GetCountryCodes cache used by
   * downstream validation. [code-search: VALIDATED_COUNTRY_HYDRATE]
   */
  const {
    loading: countryLoading,
    error: countryError,
    resolveAndSet,
  } = useSetBankCountry({
    countryCode: "CA",
    paymentPrefDetail,
  });

  const handleFormSubmission = useCallback(
    (values: EftPaymentPrefInputType) => {
      const bankCountry = selectedBankCountry ?? resolveAndSet({ countryCode: "CA" });
      setPaymentPrefDetail(mapFormStateToContextState(values, bankCountry));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick, selectedBankCountry, resolveAndSet],
  );

  if (loading || countryLoading) {
    return <LoadingIndicator />;
  }

  if (error || countryError) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.EFT}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "EftPaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
      )}
      validationSchema={EftPaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <BankDetails />
      <AccountDetails />
    </PaymentPrefLayoutForm>
  );
};
